import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  // Expose the header height as a CSS variable so sections (e.g. hero slider)
  // can size themselves to exactly fill the remaining viewport.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setVar = () => {
      // Measure the un-scrolled height for stable layout below the header.
      const h = el.offsetHeight;
      document.documentElement.style.setProperty('--header-h', `${h}px`);
    };
    setVar();
    window.addEventListener('resize', setVar);
    window.addEventListener('load', setVar);
    return () => {
      window.removeEventListener('resize', setVar);
      window.removeEventListener('load', setVar);
    };
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header ref={headerRef} className={`header ${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="header-container">
        <div className="header-left">
          <div className="header-logo">
            <Link to="/" onClick={() => setIsMenuOpen(false)}>
              <img
                src="/logonish.jpeg"
                alt="Nish Global Logo"
                className="logo-image"
              />
            </Link>
          </div>
        </div>

        <nav className={`header-nav ${isMenuOpen ? 'open' : ''}`}>
            <ul className="nav-list">
              <li>
                <Link
                  to="/"
                  className={location.pathname === '/' ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={location.pathname === '/about' ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.about')}
                </Link>
              </li>
              <li>
                <Link
                  to="/conference-chairs"
                  className={location.pathname === '/conference-chairs' ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.products')}
                </Link>
              </li>
              <li>
                <Link
                  to="/projects"
                  className={location.pathname === '/projects' ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.projects')}
                </Link>
              </li>
              <li>
                <Link
                  to="/structure-acoustic"
                  className={location.pathname === '/structure-acoustic' || location.pathname.startsWith('/structure-acoustic/') ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.structureAcoustic')}
                </Link>
              </li>
              <li>
                <Link
                  to="/technical-articles"
                  className={location.pathname === '/technical-articles' ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.technicalArticles')}
                </Link>
              </li>
              <li>
                <Link
                  to="/references"
                  className={location.pathname === '/references' ? 'active' : ''}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('menu.references')}
                </Link>
              </li>
              <li>
                  <Link
                  to="/contact"
                  className={location.pathname === '/contact' ? 'active' : ''}
                    onClick={() => setIsMenuOpen(false)}
                  >
                  {t('menu.contact')}
                  </Link>
                </li>
            </ul>
        </nav>

        <div className="header-right">
          <div className="header-actions">
            <div className="language-selector">
              <select
                className="lang-select"
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                aria-label="Select Language"
              >
                <option value="en">EN</option>
                <option value="ru">RU</option>
                <option value="ar">AR</option>
              </select>
            </div>
            <Link to="/contact" className="header-action-btn call-center-btn" onClick={() => setIsMenuOpen(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor"/>
              </svg>
              <span>{t('common.callCenter')}</span>
            </Link>
          </div>

          <button 
            className={`mobile-menu-toggle ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;

